import {
    RiAdminLine, RiBuilding2Fill, RiDeleteBinLine, RiEdit2Line,
    RiTeamLine,
    RiUserAddLine,
} from "@remixicon/react";
import React, {
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    GetUserAgentsDocument,
    GetUserGroupsDocument,
    HandleType,
    User,
    UserGroup,
    UserGroupUpdateAction,
    UserRole,
    useGetUserAgentsQuery,
    useModifyUserGroupsMembershipMutation,
    useSetUserRoleMutation,
} from "../../../../__generated__/api";
import Avatar from "../../../../components/avatar/Avatar";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import { Filters as FiltersSelector } from "../../../../components/filters/Filters";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Users from "../../../../components/pickers/users/Users";
import Select from "../../../../components/select/Select";
import Alert from "../../../../components/theme/alert/Alert";
import Badge from "../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import TableContainer from "../../../../components/theme/table";
import useRouting, { Link, Paths } from "../../../../contexts/Routing";
import SettingContainer from "../../../../components/theme/settings";

type UserWithContact = User & {contact: string | undefined; groupsList: string | undefined}
const UserTableContainer = TableContainer<UserWithContact>();

const ROLE_TO_NAME = {
    [UserRole.Administrator]: "Administrator",
    [UserRole.Agent]: "Agent",
    [UserRole.Guest]: "Guest",
};
const ROLE_OPTIONS = [
    {
        label: ROLE_TO_NAME[UserRole.Administrator],
        value: UserRole.Administrator,
    },
    {
        label: ROLE_TO_NAME[UserRole.Agent],
        value: UserRole.Agent,
    },
];

export function useUserView() {
    const [edit, setEdit] = useState<{user: User; role: UserRole; groups: UserGroup[]} | undefined>(undefined);

    const [saveUserRole] = useSetUserRoleMutation();

    const [modifyGroupMembers] = useModifyUserGroupsMembershipMutation();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: edit ? "Edit User" : "Add User",
        onHide: () => {
            setEdit(undefined);
            formTracker.reset();
        },
        onSave: async () => {
            await saveUserRole({
                refetchQueries: [
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    ids: formTracker.properties.users.value,
                    role: formTracker.properties.role.value,
                },
            });

            const originalGroupIds = edit?.groups.toIdList("id") as string[];
            const added = formTracker.properties.groups.value.exclude(originalGroupIds);
            const removed = originalGroupIds.exclude(formTracker.properties.groups.value);

            const updates = [
                ...added.map(groupId => ({
                    action: UserGroupUpdateAction.Add,
                    userId: edit?.user.id as string,
                    groupId,
                })),
                ...removed.map(groupId => ({
                    action: UserGroupUpdateAction.Remove,
                    userId: edit?.user.id as string,
                    groupId,
                })),
            ];
            if (updates.isNotEmpty) {
                await modifyGroupMembers({
                    refetchQueries: [
                        { query: GetUserGroupsDocument },
                        { query: GetUserAgentsDocument },
                    ],
                    variables: {
                        updates: {
                            updates,
                        },
                    },
                });
            }

            setEdit(undefined);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .array("users", () => (edit?.user.id ? [edit?.user.id] : undefined) ?? [])
                .property("role", () => edit?.role ?? UserRole.Agent)
                .array("groups", () => edit?.groups.toIdList("id") as string[] ?? [])
                .object("editUser", () => edit),
            dependencies: [edit],
        },
        body: tracker => (
            <>
                <FormGroup label="Users">
                    {tracker.properties.editUser.value ? (
                        <div>
                            <Avatar
                                src={tracker.properties.editUser.value.user.avatarUrl as string}
                                variant="sm-2"
                            />
                            {tracker.properties.editUser.value?.user.name}
                        </div>
                    ) : (
                        <Users
                            isMulti
                            placeholder="Select users"
                            value={tracker.properties.users.value}
                            onSelected={selected => tracker.properties.users.setValue(selected)}
                        />
                    )}
                </FormGroup>
                <FormGroup label="Role">
                    <Select
                        minRequired={1}
                        options={ROLE_OPTIONS}
                        value={!formTracker.properties.role.value ? undefined : {
                            label: ROLE_TO_NAME[formTracker.properties.role.value],
                            value: formTracker.properties.role.value,
                        }}
                        onChange={role => formTracker.properties.role.setValue(role.value as UserRole)}
                    />
                </FormGroup>

                <FormGroup label="Groups">
                    <UserGroups
                        value={formTracker.properties.groups.value}
                        onSelected={value => formTracker.properties.groups.setValue(value ?? [])}
                        isClearable
                        isMulti
                    />
                </FormGroup>

            </>
        ),
    });

    useEffect(() => {
        if (edit) {
            setOpen(true);
        }
    }, [edit]);

    return { element, setOpen, setEdit };
}
function getEmailDomain(email: string | undefined) {
    return email?.substring((email?.lastIndexOf("@") ?? 0) + 1).toLowerCase().trim();
}

export default function UserTable() {
    const {
        data, error, loading: isLoading,
    } = useGetUserAgentsQuery();

    const { createHistoryPush } = useRouting();
    const [saveUserRole] = useSetUserRoleMutation();

    const userData = useMemo(() => data?.users?.map(user => ({
        ...user,
        groupsList: user.userGroups?.map(row => row.name).join(", "),
        contact: user.handles?.find(row => row.type === HandleType.Email)?.email?.email
                ?? user.handles?.find(row => row.application)?.application?.displayText,
    }) as UserWithContact), [data]);

    const emailDomains = useMemo(
        () => userData?.mapFilter(row => getEmailDomain(row.contact))
            .filterNull()
            .dedupe()
            .map(domain => ({
                value: domain,
                label: domain as string,
            })),
        [userData],
    );

    const { element: deleteElement, setDeleting } = useDeleteConfirm<UserWithContact>(
        async user => {
            await saveUserRole({
                refetchQueries: [
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    ids: [user.id],
                    role: UserRole.Guest,
                },
            });
        },
        user => user.name,
        "Remove User Permissions",
        "Deactivating this user will only remove their permissions, but not delete the user from "
        + "the system. Are you sure you want to deactivate",
        "Remove Permissions",
    );

    const { element, setEdit, setOpen } = useUserView();

    const purchasedCount = data?.portal?.billing?.isSetup ? data?.portal?.billing?.purchasedUserCount : undefined;
    const isOver = purchasedCount != null && purchasedCount < (data?.users?.length ?? 0);

    return (
        <SettingContainer.Section
            sectionKey="users"
            title="Users"
            shouldDisplayTitle={false}
        >
            <SettingContainer.Setting
                title="Users"
                description="Manage administrators"
                permissions={SettingContainer.Setting.Permissions.Administrator}
                isFullWidth
            >

                {deleteElement}
                {element}
                {purchasedCount && isOver && (
                    <Alert
                        variant="danger"
                        actionText="Get More Seats"
                        onActionClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING, { setting: "billing" })}
                    >
                        You have 0 seats remaining.
                    </Alert>
                )}
                <UserTableContainer data={userData} isLoading={isLoading} error={error}>
                    <UserTableContainer.Empty>
                        <UserTableContainer.Empty.Default
                            text="To create a user, click to create a new user below"
                            title="Create an Agent"
                            icon={<RiUserAddLine />}
                            onClick={() => setOpen(true)}
                            buttonText="Add Agent"
                        />
                    </UserTableContainer.Empty>
                    <UserTableContainer.TableControls>
                        <UserTableContainer.TableControls.Search
                            search={(query, user) => user.name?.toLocaleLowerCase().includes(query)
                                                    || user.role?.toLocaleLowerCase().includes(query)
                                                    || user.organization?.name?.toLocaleLowerCase().includes(query)
                                                    || user.contact?.toLocaleLowerCase().includes(query)
                                                    || user.userGroups.some(row => row.name.toLocaleLowerCase().includes(query))}
                        />
                        <UserTableContainer.TableControls.Seperator>
                            {purchasedCount && !isOver && (
                                <Alert variant="outline" className="m-0 border-0">
                                    You are using
                                    {" "}
                                    {data?.users?.length}
                                    {" "}
                                    of
                                    {" "}
                                    {purchasedCount}
                                    {" "}
                                    purchased seats.
                                    {" "}
                                    <Link to={Paths.ADMINISTRATOR_SETTING} params={{ setting: "billing" }}>
                                        Get More seats
                                    </Link>
                                </Alert>
                            )}

                        </UserTableContainer.TableControls.Seperator>

                        <UserTableContainer.TableControls.Filters />

                        <UserTableContainer.TableControls.Filter
                            defaultValue={new Set<string>()}
                            deserialize={UserTableContainer.TableControls.Filter.Serializers.deserializeSet}
                            serialize={UserTableContainer.TableControls.Filter.Serializers.serializeSet}
                            filterKey="roles"
                            onFilter={(item, selected) => item.role && selected.has(item.role)}
                        >
                            {(currentValue, setValue) => (
                                <FiltersSelector
                                    toggleText="Role"
                                    className=""
                                    icon={<RiAdminLine />}
                                    categories={ROLE_OPTIONS}
                                    searchMinimum={5}
                                    selectedCategories={currentValue}
                                    onSelected={setValue}
                                />
                            )}

                        </UserTableContainer.TableControls.Filter>
                        <UserTableContainer.TableControls.Filter
                            defaultValue={new Set<string>()}
                            deserialize={UserTableContainer.TableControls.Filter.Serializers.deserializeSet}
                            serialize={UserTableContainer.TableControls.Filter.Serializers.serializeSet}
                            filterKey="emails"
                            onFilter={(item, selected) => selected.has(getEmailDomain(item.contact) ?? "")}
                        >
                            {(currentValue, setValue) => (
                                <FiltersSelector
                                    toggleText="Domains"
                                    className=""
                                    icon={<RiBuilding2Fill />}
                                    categories={emailDomains}
                                    searchMinimum={5}
                                    selectedCategories={currentValue}
                                    onSelected={setValue}
                                />
                            )}

                        </UserTableContainer.TableControls.Filter>
                        <UserTableContainer.TableControls.Filter
                            defaultValue={[] as string[]}
                            deserialize={UserTableContainer.TableControls.Filter.Serializers.deserializeArray}
                            serialize={UserTableContainer.TableControls.Filter.Serializers.serializeArray}
                            filterKey="groups"
                            onFilter={(item, selected) => selected.isEmpty
                            || selected.union(item.userGroups.toIdList("id")).isNotEmpty}
                        >
                            {(currentValue, setValue) => (
                                <UserGroups
                                    toggleText="Group"
                                    className=""
                                    icon={<RiTeamLine />}
                                    searchMinimum={5}
                                    isMulti
                                    shouldShowDropdown
                                    value={currentValue}
                                    onSelected={setValue}
                                />
                            )}

                        </UserTableContainer.TableControls.Filter>
                        <UserTableContainer.TableControls.SortBy />

                        <UserTableContainer.TableControls.Create
                            text="Add User"
                            disabled={isOver}
                            aria-disabled={isOver}
                            title={isOver ? "You have reached the maximum number of users." : undefined}
                            onClick={() => setOpen(true)}
                        />
                    </UserTableContainer.TableControls>

                    <UserTableContainer.Table>
                        <UserTableContainer.TableValue
                            columnKey="userName"
                            title="User name"
                            sortBy={item => item.name}
                        >
                            {item => (
                                <>
                                    <Avatar src={item.avatarUrl as string} variant="sm-2" />
                                    {item.name}
                                </>
                            )}
                        </UserTableContainer.TableValue>
                        <UserTableContainer.TableValue
                            columnKey="role"
                            title="Role"
                            sortBy={item => item.role}
                        >
                            {item => (
                                <Badge
                                    variant={item.role === UserRole.Administrator ? "info" : "gray"}
                                >
                                    {item.role?.toLowerCase().capitalize()}
                                </Badge>
                            )}
                        </UserTableContainer.TableValue>
                        <UserTableContainer.TableValue
                            columnKey="contact"
                            title="Contact"
                            sortBy={item => item.contact}
                        >
                            {item => item.contact}
                        </UserTableContainer.TableValue>
                        <UserTableContainer.TableValue
                            columnKey="groups"
                            title="Groups"
                            sortBy={item => item.groupsList}
                            className={SettingContainer.Setting.BadgeListClassName}
                        >
                            {item => item.userGroups.map(group => (
                                <Badge
                                    key={group.id}
                                    variant="gray"
                                >
                                    {group.name}
                                </Badge>
                            ))}
                        </UserTableContainer.TableValue>
                        <UserTableContainer.Action
                            icon={<RiDeleteBinLine />}
                            altText="Delete User"
                            onClick={setDeleting}
                        />
                        <UserTableContainer.Action
                            icon={<RiEdit2Line />}
                            altText="Edit User"
                            onClick={user => {
                                setEdit({
                                    role: user.role ?? UserRole.Agent,
                                    user,
                                    groups: user.userGroups,
                                });
                            }}
                        />

                    </UserTableContainer.Table>
                </UserTableContainer>
            </SettingContainer.Setting>
        </SettingContainer.Section>
    );
}
